import React, { useEffect, useState } from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import { connect } from "react-redux";
import { IWithRouterProps } from "../../../../shared/interfaces/with-router-props.interface";
import { withRouter } from "react-router-dom";
import { performGetRequest } from "../../../../shared/redux/middleware/api.middleware";
import { ApiEndpoint } from "../../../../shared/constants/api-endpoint.enum";
import { AffiliateActionTypes } from "../../../../shared/redux/affiliate/affiliate.action-types";
import { IState } from "../../../../shared/interfaces/state.interface";
import { FormService } from "../../../../shared/services/form.service";
import { Form } from "../../../form/form";
import { IFieldChangeEvent } from "../../../form/interfaces/field-single.interface";
import {
  selectAffiliateOwnerAgentObjects,
  selectAffiliateTenancyAgentObjects
} from "../../../../shared/redux/affiliate/affiliate.selectors";
import {
  AffiliateOwnerAgentObjectsFormDefinition,
  AffiliateTenancyAgentObjectsFormDefinition
} from "../../../form/definitions/affiliate/agentobjects/affiliate-agent-objects.form-definition";
import { useUser } from "../../../../contexts/user.context";
import { LoadingStatus } from "../../../../shared/constants/loading-status.enum";
import { HttpService } from "../../../../shared/services/http.service";
import { AffiliateActions } from "../../../../shared/redux/affiliate/affiliate.actions";

interface IAffiliateDetailAgentViewProps extends IWithRouterProps<{ affiliateId: number }> {
  ownerAgentObjects: any;
  fetchOwnerAgentObjects: (affiliateId: number) => Promise<any>;
  updateOwnerAgentObjects: (data) => Promise<any>;

  tenancyAgentObjects: any;
  fetchTenancyAgentObjects: (affiliateId: number) => Promise<any>;
  updateTenancyAgentObjects: (data) => Promise<any>;
}

export const AffiliateDetailAgentObjectsView = React.memo(withRouter((props: IAffiliateDetailAgentViewProps) => {
  const {
    match,

    ownerAgentObjects,
    fetchOwnerAgentObjects,
    updateOwnerAgentObjects,

    tenancyAgentObjects,
    fetchTenancyAgentObjects,
    updateTenancyAgentObjects
  } = props;

  const [affiliateId, setAffiliateId] = useState<number>(null);
  const [ownerFormDefinition, setOwnerFormDefinition] = useState(null);
  const [tenancyFormDefinition, setTenancyFormDefinition] = useState(null);

  const { getGroups } = useUser();

  const [writePermissions, setWritePermissions] = useState(false);

  useEffect(() => {
    if (getGroups()?.includes("admin") || getGroups()?.includes("editor")) {
      setWritePermissions(true);
    }
  }, [getGroups]);

  useEffect(() => {
    setAffiliateId(match.params.affiliateId);
  }, [match]);

  useEffect(() => {
    if (affiliateId !== null) {
      fetchOwnerAgentObjects(affiliateId).then();
      fetchTenancyAgentObjects(affiliateId).then();
    }
  }, [affiliateId, fetchOwnerAgentObjects, fetchTenancyAgentObjects]);

  useEffect(() => {
    const formService = new FormService();
    const updatedFormDefinition = formService.updateFormSets(AffiliateOwnerAgentObjectsFormDefinition, { agentObjects: ownerAgentObjects.toJS() }, { readonly: !writePermissions });

    setOwnerFormDefinition(updatedFormDefinition);
  }, [ownerAgentObjects, writePermissions]);


  useEffect(() => {
    const formService = new FormService();
    const updatedFormDefinition = formService.updateFormSets(AffiliateTenancyAgentObjectsFormDefinition, { agentObjects: tenancyAgentObjects.toJS() }, !writePermissions ? { readonly: true } : {});

    setTenancyFormDefinition(updatedFormDefinition);
  }, [tenancyAgentObjects, writePermissions]);

  const handleOwnerInputChange = async (changeEvent: IFieldChangeEvent) => {
    if (!writePermissions) {
      return;
    }

    const updateData = changeEvent.value;

    updateOwnerAgentObjects({
      affiliateId,
      [changeEvent.name]: updateData
    });
  };

  const handleTenancyInputChange = async (changeEvent: IFieldChangeEvent) => {
    if (!writePermissions) {
      return;
    }

    const updateData = changeEvent.value;

    updateTenancyAgentObjects({
      affiliateId,
      [changeEvent.name]: updateData
    });
  };

  return (
    <Container fluid>
      <Row>
        <Form showButtonControls={false}
              refetchSourceData={async () => await fetchOwnerAgentObjects(affiliateId)}
              changeHandler={handleOwnerInputChange}
              formDefinition={ownerFormDefinition}/>
      </Row>
      <Row>
        <Form showButtonControls={false}
              refetchSourceData={async () => await fetchTenancyAgentObjects(affiliateId)}
              changeHandler={handleTenancyInputChange}
              formDefinition={tenancyFormDefinition}/>
      </Row>
    </Container>
  );
}));

const mapStateToProps = (state: IState) => {
  return {
    ownerAgentObjects: selectAffiliateOwnerAgentObjects(state),
    tenancyAgentObjects: selectAffiliateTenancyAgentObjects(state)
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  fetchOwnerAgentObjects: (affiliateId: number) => dispatch(
    performGetRequest(
      `${ApiEndpoint.AFFILIATE}/${affiliateId}/owneragentobjects`,
      AffiliateActionTypes.FETCH_IN_PROGRESS,
      AffiliateActionTypes.SET_AFFILIATE_OWNER_AGENT_OBJECTS,
      AffiliateActionTypes.FETCH_FINISHED
    )
  ),
  updateOwnerAgentObjects: async ({ affiliateId, ...data }) => {
    await dispatch({
      type: AffiliateActionTypes.FETCH_IN_PROGRESS,
      loadingStatus: LoadingStatus.IN_PROGRESS
    });

    const res = await HttpService.performPutRequest(`${ApiEndpoint.AFFILIATE}/${affiliateId}/owneragentobjects`, data);

    await dispatch({
      type: AffiliateActionTypes.UPDATE_AFFILIATE_OWNER_AGENT_OBJECTS,
      payload: res?.data
    });

    await dispatch({
      type: AffiliateActionTypes.FETCH_FINISHED,
      loadingStatus: LoadingStatus.READY
    });

    dispatch(AffiliateActions.touch(res?.meta?.updatedByTitle));
  },
  fetchTenancyAgentObjects: (affiliateId: number) => dispatch(
    performGetRequest(
      `${ApiEndpoint.AFFILIATE}/${affiliateId}/tenancyagentobjects`,
      AffiliateActionTypes.FETCH_IN_PROGRESS,
      AffiliateActionTypes.SET_AFFILIATE_TENANCY_AGENT_OBJECTS,
      AffiliateActionTypes.FETCH_FINISHED
    )
  ),
  updateTenancyAgentObjects: async ({ affiliateId, ...data }) => {
    await dispatch({
      type: AffiliateActionTypes.FETCH_IN_PROGRESS,
      loadingStatus: LoadingStatus.IN_PROGRESS
    });

    const res = await HttpService.performPutRequest(`${ApiEndpoint.AFFILIATE}/${affiliateId}/tenancyagentobjects`, data);

    await dispatch({
      type: AffiliateActionTypes.UPDATE_AFFILIATE_TENANCY_AGENT_OBJECTS,
      payload: res?.data
    });

    await dispatch({
      type: AffiliateActionTypes.FETCH_FINISHED,
      loadingStatus: LoadingStatus.READY
    });

    dispatch(AffiliateActions.touch(res?.meta?.updatedByTitle));
  }
});

export const AffiliateDetailAgentObjectsViewWithStore = connect(mapStateToProps, mapDispatchToProps)(AffiliateDetailAgentObjectsView);
